/** Subtract mapped tunnel prisms from building faces without removing city blocks. */
import type { TunnelVolume } from './tunnelmesh.js';

type Vertex = number[]; // xyz, linear RGB, glow
interface Cut { planes: number[][]; box: number[] }
const CELL = 32;

/** Keep outside fragments while clipping the remainder against a convex solid. */
function subtract(poly: Vertex[], cut: Cut): Vertex[][] {
  let inside = poly;
  const outside: Vertex[][] = [];
  for (const [nx, ny, nz, d] of cut.planes) {
    const yes: Vertex[] = [], no: Vertex[] = [];
    for (let i = 0; i < inside.length; i++) {
      const a = inside[i], b = inside[(i + 1) % inside.length];
      const da = a[0] * nx + a[1] * ny + a[2] * nz + d;
      const db = b[0] * nx + b[1] * ny + b[2] * nz + d;
      (da >= 0 ? yes : no).push(a);
      if ((da < 0) !== (db < 0)) {
        const t = da / (da - db), p = a.map((v, k) => v + (b[k] - v) * t);
        yes.push(p); no.push(p);
      }
    }
    if (no.length >= 3) outside.push(no);
    inside = yes;
    if (inside.length < 3) break;
  }
  return outside;
}

export function cutTunnelOpenings(positions: Float32Array, colors: Uint8Array, glow: Uint8Array,
  indices: Uint32Array, volume: TunnelVolume): { positions: Float32Array; colors: Uint8Array; glow: Uint8Array; indices: Uint32Array } {
  const unchanged = { positions, colors, glow, indices };
  if (volume.empty || indices.length === 0) return unchanged;
  const cuts: Cut[] = [];
  const cells = new Map<string, number[]>();
  for (const { outline, bottom, top } of volume.openings()) {
    const cx = outline.reduce((s, p) => s + p[0], 0) / outline.length;
    const cz = outline.reduce((s, p) => s + p[1], 0) / outline.length;
    const planes: number[][] = [[0, 1, 0, -bottom]];
    if (Number.isFinite(top)) planes.push([0, -1, 0, top]);
    for (let i = 0; i < outline.length; i++) {
      const [ax, az] = outline[i], [bx, bz] = outline[(i + 1) % outline.length];
      const nx = -(bz - az), nz = bx - ax, d = -nx * ax - nz * az;
      if (Math.hypot(nx, nz) < 1e-6) continue;
      const sign = nx * cx + nz * cz + d >= 0 ? 1 : -1;
      planes.push([nx * sign, 0, nz * sign, d * sign]);
    }
    if (planes.length < 5) continue;
    const box = [Math.min(...outline.map(p => p[0])), Math.min(...outline.map(p => p[1])),
      Math.max(...outline.map(p => p[0])), Math.max(...outline.map(p => p[1])), bottom, top];
    const ci = cuts.length;
    cuts.push({ planes, box });
    for (let x = Math.floor(box[0] / CELL); x <= Math.floor(box[2] / CELL); x++) {
      for (let z = Math.floor(box[1] / CELL); z <= Math.floor(box[3] / CELL); z++) {
        const key = `${x},${z}`, list = cells.get(key) ?? [];
        list.push(ci); cells.set(key, list);
      }
    }
  }
  const outP = Array.from(positions), outC = Array.from(colors), outG = Array.from(glow), outI: number[] = [];
  let changed = false;
  for (let t = 0; t < indices.length; t += 3) {
    const ids = [indices[t], indices[t + 1], indices[t + 2]];
    const tri = ids.map(v => [...positions.subarray(v * 3, v * 3 + 3), ...colors.subarray(v * 3, v * 3 + 3), glow[v] ?? 0]);
    const xmin = Math.min(...tri.map(p => p[0])), xmax = Math.max(...tri.map(p => p[0]));
    const zmin = Math.min(...tri.map(p => p[2])), zmax = Math.max(...tri.map(p => p[2]));
    const ymin = Math.min(...tri.map(p => p[1])), ymax = Math.max(...tri.map(p => p[1]));
    const near = new Set<number>();
    for (let x = Math.floor(xmin / CELL); x <= Math.floor(xmax / CELL); x++) {
      for (let z = Math.floor(zmin / CELL); z <= Math.floor(zmax / CELL); z++) {
        for (const ci of cells.get(`${x},${z}`) ?? []) near.add(ci);
      }
    }
    let pieces: Vertex[][] = [tri];
    for (const ci of near) {
      const cut = cuts[ci], b = cut.box;
      if (xmax < b[0] || xmin > b[2] || zmax < b[1] || zmin > b[3] || ymax < b[4] || ymin > b[5]) continue;
      // No intersection if the triangle lies wholly outside any one plane.
      if (cut.planes.some(([nx, ny, nz, d]) => tri.every(p => p[0] * nx + p[1] * ny + p[2] * nz + d < -1e-6))) continue;
      pieces = pieces.flatMap(p => subtract(p, cut));
      if (pieces.length === 0) break;
    }
    if (pieces.length === 1 && pieces[0] === tri) { outI.push(...ids); continue; }
    changed = true;
    for (const p of pieces) {
      const start = outP.length / 3;
      for (const v of p) { outP.push(v[0], v[1], v[2]); outC.push(v[3], v[4], v[5]); outG.push(Math.round(v[6])); }
      for (let i = 1; i + 1 < p.length; i++) outI.push(start, start + i, start + i + 1);
    }
  }
  return changed ? { positions: new Float32Array(outP), colors: new Uint8Array(outC.map(Math.round)), glow: new Uint8Array(outG), indices: new Uint32Array(outI) } : unchanged;
}
